import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * Handles the redirect back from `auth.emergentagent.com`.
 * URL fragment shape: `#session_id=<token>` (may include a `?next=...` we injected).
 * On success, redirects to /portal (or the `next` param).
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const [error, setError] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    const next = params.get("next") || "/portal";

    if (!sessionId) {
      setError("Missing session_id in callback URL.");
      return;
    }

    (async () => {
      try {
        await exchangeGoogleSession(sessionId);
        // Clean the hash and go to the target page
        window.history.replaceState(null, "", window.location.pathname);
        navigate(next, { replace: true });
      } catch (err) {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="page-auth-callback">
      <BrandStripe thickness="h-1.5" />
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="max-w-md w-full text-center">
          <Logo size={44} />
          {!error ? (
            <>
              <div className="mt-10 w-14 h-14 grid place-items-center bg-[#00A651]/10 text-[#00A651] rounded-sm mx-auto">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
              <p className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[#E88C2F]">
                Google Sign-In
              </p>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-black mt-2">
                Securing your session…
              </h1>
              <p className="mt-3 text-gray-600 font-sub italic">
                <ShieldCheck className="inline w-4 h-4 mr-1 text-[#00A651]" />
                Verifying your Google credentials with our authentication service.
              </p>
            </>
          ) : (
            <>
              <div className="mt-10 w-14 h-14 grid place-items-center bg-[#ED1C24]/10 text-[#ED1C24] rounded-sm mx-auto">
                <XCircle className="w-6 h-6" />
              </div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-black mt-6">
                We couldn&apos;t sign you in
              </h1>
              <p className="mt-3 text-[#ED1C24] font-bold uppercase tracking-wider text-xs" data-testid="auth-callback-error">
                {error}
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                className="mt-8 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-sm"
                data-testid="auth-callback-retry"
              >
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </main>
      <BrandStripe thickness="h-1.5" />
    </div>
  );
}
